import { useMemo, useState } from 'react';
import { FaCircleQuestion } from 'react-icons/fa6';
import { LiaTimesSolid } from 'react-icons/lia';
import {
  Button,
  Modal,
  PdfViewer,
  Spinner,
  Tooltip,
  TooltipTriggerWrapper,
  Typography,
} from '@breezeai-frontend/cargo-ui';
import { startCase } from 'lodash';

import { usePlatform } from '../../../context/PlatformContext';

export const CertificatePdfOverrideModal = ({
  policyId,
  certificateId,
  certificateUrl,
  isPdfOverideOpen,
  setIsPdfOverrideOpen,
  onSave,
  isSaving,
}: {
  policyId: string;
  certificateId: string;
  certificateUrl: string;
  isPdfOverideOpen: boolean;
  setIsPdfOverrideOpen: (value: boolean) => void;
  onSave?: (data: Record<string, string>) => void;
  isSaving?: boolean;
}) => {
  const [fields, setFields] = useState<Record<string, string>>();
  const { isWtw } = usePlatform();
  const [editableFields, setEditableFields] = useState<string[]>([]);

  const editableFieldsLabels = useMemo(
    () =>
      editableFields.map((field) => {
        if (field === 'load') return 'Place of Loading';
        if (field === 'discharge') return 'Place of Discharge';
        return startCase(field.replace(/_/g, ' '));
      }),
    [editableFields],
  );

  const hasChanges = Object.values(fields ?? {}).some((value) => value !== '');

  return (
    <Modal
      // TODO: Heading is not used, but logs warnings in console - remove when fixed
      heading={<></>}
      isDismissable
      isOpen={isPdfOverideOpen}
      onOpenChange={(value) => {
        setIsPdfOverrideOpen(value);
      }}
      onExit={() => setIsPdfOverrideOpen(false)}
      className="absolute top-4 max-w-[750px] w-full flex flex-col justify-between justify-items-start p-0 bg-[#F2F3F6]"
    >
      <div className="px-4 border-b border-gray-300 w-[750px] grid grid-cols-[1fr_auto] items-center pb-4">
        <div className="flex gap-0.5 flex-col justify-items-start w-full">
          <div className="flex gap-2 items-center">
            <Typography level="h4">
              Edit Display Details in PDF Certificate
            </Typography>
            <TooltipTriggerWrapper delay={0}>
              <Button variant="icon">
                <FaCircleQuestion className="inline-block size-4 text-text-secondary" />
              </Button>
              <Tooltip
                placement="top"
                showArrow={true}
                className={'bg-gray-900 max-w-lg text-white'}
              >
                <b>Edit available fields directly in the PDF.</b> <br />{' '}
                Editable fields: {`${editableFieldsLabels.join(', ')}`}
                . <br />
              </Tooltip>
            </TooltipTriggerWrapper>
          </div>

          <Typography level="base" color="secondary">
            Edit directly in the PDF. Saved changes will be shown in the PDF but
            not change original values.
          </Typography>
          <a
            className="self-start text-buttons-ghost-text font-semibold text-sm"
            href={
              isWtw
                ? `certificates/update/${certificateId}/${policyId}`
                : `/policies/${policyId}/${certificateId}/edit`
            }
          >
            Update & Reissue Certificate
          </a>
        </div>
        <Button
          variant="ghost"
          leftIcon={<LiaTimesSolid className="size-4 text-text-secondary" />}
          onPress={() => {
            setIsPdfOverrideOpen(false);
          }}
        />
      </div>

      <div className="relative w-[750px] h-[550px]">
        <PdfViewer
          file={certificateUrl}
          initialScale={1.03}
          onChange={setFields}
          onInitialize={(initialValues) => {
            setEditableFields(Object.keys(initialValues));
          }}
        />
      </div>
      <div className="flex justify-end px-6  items-center w-[750px] pt-4 gap-2 border-t border-gray-300">
        <Button
          variant="ghost"
          label="Cancel"
          onPress={() => {
            setIsPdfOverrideOpen(false);
          }}
        />
        <Button
          variant="primary"
          label={isSaving ? 'Saving...' : 'Save Changes'}
          leftIcon={isSaving && <Spinner className="size-4" />}
          onPress={() => onSave?.(fields || {})}
          customStyles="min-w-32"
          isDisabled={!hasChanges || isSaving}
        />
      </div>
    </Modal>
  );
};
